import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import { SkeletonTable } from '../../components/common/Skeleton';
import CopyButton from '../../components/common/CopyButton';
import { ShieldCheck, Search, CheckCircle2, XCircle, ArrowUpRight, Hash, Send } from 'lucide-react';

const WithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selected withdrawal for admin processing modal
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [payoutTxid, setPayoutTxid] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState({ type: '', text: '' });

  const fetchAdminWithdrawals = async () => {
    try {
      const res = await axiosClient.get('/withdrawal/admin/all');
      setWithdrawals(res.data.withdrawals || []);
    } catch (err) {
      console.error('[Admin Withdrawals Fetch Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminWithdrawals();
  }, []);

  const handleProcess = async (newStatus) => {
    if (!selectedWithdrawal) return;
    setProcessing(true);
    setFeedbackMsg({ type: '', text: '' });

    try {
      await axiosClient.patch(`/withdrawal/admin/${selectedWithdrawal._id}/process`, {
        status: newStatus,
        txid: payoutTxid,
        adminNotes
      });
      setFeedbackMsg({ 
        type: 'success', 
        text: `Withdrawal of $${selectedWithdrawal.amount} marked as ${newStatus}. ${newStatus === 'rejected' ? 'Wallet balance refunded to user.' : ''}` 
      });
      setSelectedWithdrawal(null);
      setPayoutTxid('');
      setAdminNotes('');
      fetchAdminWithdrawals();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to process withdrawal request.';
      setFeedbackMsg({ type: 'error', text: msg });
    } finally {
      setProcessing(false);
    }
  };

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchSearch = 
      w.walletAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.userId?.userIdTag?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchStatus = statusFilter === 'all' || w.status === statusFilter;

    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6">
      {/* Title Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#121215] p-5 rounded-2xl border border-[#27272A] shadow-card gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-[#F97316]" />
            <h2 className="text-lg font-extrabold text-[#FFFFFF]">Crypto Withdrawal Payout Panel</h2>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">
            Review client withdrawal requests, record manual crypto transfer TXID hashes, or reject with automatic wallet balance refund reversal.
          </p>
        </div>

        {/* Search & Filter Controls */}
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search user, address, User ID..."
              className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#F97316]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#09090B] border border-[#27272A] text-[#FFFFFF] text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#F97316]"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending Payout</option>
            <option value="completed">Completed Payout</option>
            <option value="rejected">Rejected / Refunded</option>
          </select>
        </div>
      </div>

      {feedbackMsg.text && (
        <div className={`p-4 rounded-2xl border text-xs font-semibold ${
          feedbackMsg.type === 'success' 
            ? 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]' 
            : 'bg-[#EF4444]/15 border-[#EF4444]/30 text-[#EF4444]'
        }`}>
          {feedbackMsg.text}
        </div>
      )}

      {/* Admin Withdrawal Requests Table */}
      <Card title={`Client Withdrawal Requests (${filteredWithdrawals.length})`} subtitle="Click Review to record payout TXID or reject request">
        {loading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : filteredWithdrawals.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#9CA3AF]">No withdrawal requests match search filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Amount ($)</th>
                  <th className="py-3 px-4">Network</th>
                  <th className="py-3 px-4">Destination Wallet Address</th>
                  <th className="py-3 px-4">Payout Status</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A] text-xs">
                {filteredWithdrawals.map((w) => (
                  <tr key={w._id} className="hover:bg-[#1A1A1E] transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center space-x-1.5">
                        <span className="font-bold text-[#FFFFFF]">{w.userId?.name || 'Unknown'}</span>
                        {w.userId?.userIdTag && (
                          <span className="inline-flex items-center space-x-1 font-mono text-[10px] text-[#F59E0B]">
                            <span>({w.userId?.userIdTag})</span>
                            <CopyButton text={w.userId?.userIdTag} />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                        <span>{w.userId?.email}</span>
                        {w.userId?.email && <CopyButton text={w.userId?.email} />}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-bold text-[#EF4444]">
                      -${w.amount.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#9CA3AF]">
                      {w.network}
                    </td>
                    <td className="py-3.5 px-4 font-mono text-[#FFFFFF]">
                      <div className="flex items-center space-x-1.5">
                        <span title={w.walletAddress}>{w.walletAddress.substring(0, 14)}...</span>
                        <CopyButton text={w.walletAddress} />
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <StatusBadge status={w.status} label={w.status} />
                    </td>
                    <td className="py-3.5 px-4 text-[#9CA3AF]">
                      {new Date(w.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <Button
                        variant={w.status === 'pending' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => {
                          setSelectedWithdrawal(w);
                          setPayoutTxid(w.txid || '');
                          setAdminNotes(w.adminNotes || '');
                        }}
                      >
                        {w.status === 'pending' ? 'Process Payout' : 'View Details'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Processing Modal */}
      {selectedWithdrawal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF]">Process Crypto Payout</h3>
                <p className="text-xs text-[#9CA3AF] flex items-center space-x-1.5 mt-0.5">
                  <span>User: <span className="font-bold text-[#FFFFFF]">{selectedWithdrawal.userId?.name}</span></span>
                  {selectedWithdrawal.userId?.userIdTag && (
                    <span className="inline-flex items-center space-x-1 font-mono text-[#F59E0B]">
                      <span>({selectedWithdrawal.userId?.userIdTag})</span>
                      <CopyButton text={selectedWithdrawal.userId?.userIdTag} />
                    </span>
                  )}
                </p>
              </div>
              <button onClick={() => setSelectedWithdrawal(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold">✕</button>
            </div>

            <div className="p-4 bg-[#09090B] rounded-2xl border border-[#27272A] space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">Payout Amount:</span>
                <span className="font-bold text-[#EF4444]">${selectedWithdrawal.amount.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9CA3AF]">Network:</span>
                <span className="font-mono text-[#FFFFFF]">{selectedWithdrawal.network}</span>
              </div>
              <div>
                <span className="text-[#9CA3AF] block mb-1">Destination Crypto Wallet Address:</span>
                <div className="font-mono text-[#F59E0B] bg-[#121215] p-2.5 rounded-xl border border-[#27272A] break-all font-bold flex items-center justify-between gap-2">
                  <span>{selectedWithdrawal.walletAddress}</span>
                  <CopyButton text={selectedWithdrawal.walletAddress} />
                </div>
              </div>
            </div>

            {selectedWithdrawal.status === 'pending' && (
              <form className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Payout Blockchain Transaction Hash (TXID)</label>
                  <div className="relative">
                    <Hash className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-3.5" />
                    <input
                      type="text"
                      value={payoutTxid}
                      onChange={(e) => setPayoutTxid(e.target.value)}
                      placeholder="Paste payout transaction TXID hash..."
                      className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl pl-10 pr-4 py-3 text-xs font-mono focus:outline-none focus:border-[#F97316]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#9CA3AF] mb-1.5">Admin Payout Notes</label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Notes (e.g. Sent via hot wallet)"
                    rows={2}
                    className="w-full bg-[#09090B] border border-[#27272A] text-[#FFFFFF] placeholder-[#9CA3AF]/40 rounded-xl p-3 text-xs focus:outline-none focus:border-[#F97316]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#27272A]">
                  <Button
                    variant="danger"
                    size="sm"
                    type="button"
                    onClick={() => handleProcess('rejected')}
                    disabled={processing}
                  >
                    Reject & Refund Wallet
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    type="button"
                    onClick={() => handleProcess('completed')}
                    disabled={processing}
                  >
                    Complete Payout
                  </Button>
                </div>
              </form>
            )}

            {selectedWithdrawal.status !== 'pending' && (
              <div className="p-3 bg-[#09090B] rounded-xl text-center text-xs text-[#9CA3AF]">
                This withdrawal request is <span className="font-bold uppercase text-[#FFFFFF]">{selectedWithdrawal.status}</span>.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalManagement;
